/**
 * @file mindmapEvidence.ts
 * @brief Resolve a mindmap node back to the intel item it mirrors, and to that
 *        item's evidence anchor.
 *
 * @details
 * Mindmap nodes carry **no anchor of their own** — the synthesis prompt builds
 * them as a restatement of the intel it already extracted ("mirror all
 * key_decisions", "mirror all todos", risks for concerns). So a node's evidence
 * is its source item's evidence, and the only way to get from one to the other
 * on an existing meeting is to match the text. This module does that matching,
 * client-side, so meetings compiled before today still open a quote — no
 * backend change, no `regenerate-intelligence --force` sweep.
 *
 * The matching is a token Dice coefficient over normalised text, with a
 * containment shortcut. It is language-agnostic on purpose (cross-script
 * folding, no keyword lists) — mindmap labels and intel prose are both written
 * in the meeting's output language, so they fold to the same alphabet; see the
 * same principle in BACKEND anchor.service.mjs.
 *
 * **The honesty rule is the whole point of this file** (docs/product/
 * meeting-intel-evidence-anchor-plan.md): a node that cannot be matched, or
 * whose item has `confidence: "none"`, must render as *unanchored*. It must not
 * borrow a nearby item's quote, and it must not play. A wrong quote under a
 * confident clock is worse than no quote — that is the bug the anchor work was
 * done to kill, and text matching is exactly the place it could come back.
 * Hence: a floor on the score, and a required margin over the runner-up, so an
 * ambiguous label resolves to nothing rather than to a coin flip.
 *
 * If mindmap nodes ever grow their own stored anchors, `nodeAnchorOverride`
 * below is where that wins — the matching then becomes the fallback for old
 * meetings only.
 *
 * @dependencies
 * - ./meetingEvidence: the shared anchor resolver (never re-implement it here)
 *
 * @references
 * - BACKEND/src/features/boardroom/synthesis.service.mjs (the mindmap prompt —
 *   the reason decision/action/risk nodes mirror the intel arrays)
 * - features/company-meetings/components/MeetingMindmapEvidence.tsx (renderer)
 *
 * @author DES
 * @date 04-08-2026
 */

import type { MeetingDetail } from "../schemas";
import {
  buildSegmentStarts,
  pairDecisions,
  resolveVoiceAnchor,
  splitRiskLines,
  type VoiceAnchor,
} from "./meetingEvidence";
import type { MindmapNodeData } from "./mindmapGraph";

// ----------------------------------------------------------------------
// Text normalisation + similarity
// ----------------------------------------------------------------------

/**
 * Arabic-script variants that carry no meaning difference for matching. Urdu
 * and Arabic write several of these letters with different code points, and
 * synthesis output has been seen using either — see the same folding on the
 * backend side in anchor.service.mjs.
 */
const SCRIPT_FOLD: Record<string, string> = {
  "ي": "ی", // ARABIC YEH      -> FARSI YEH
  "ى": "ی", // ALEF MAKSURA    -> FARSI YEH
  "ك": "ک", // ARABIC KAF      -> KEHEH
  "ة": "ہ", // TEH MARBUTA     -> HEH GOAL
  "ه": "ہ", // HEH             -> HEH GOAL
  "أ": "ا", // ALEF WITH HAMZA ABOVE -> ALEF
  "إ": "ا", // ALEF WITH HAMZA BELOW -> ALEF
  "آ": "ا", // ALEF WITH MADDA -> ALEF
};
const SCRIPT_FOLD_RE = new RegExp(
  `[${Object.keys(SCRIPT_FOLD).join("")}]`,
  "g",
);

/**
 * Lowercase, strip diacritics and punctuation (Latin *and* Arabic), fold the
 * script variants, collapse whitespace. Deliberately not stemmed: an English
 * stemmer would do nothing for Urdu and would make the two languages behave
 * differently on the same screen.
 */
export function normalizeForMatch(raw: string): string {
  return raw
    .normalize("NFKD")
    .replace(/[̀-ͯ]/g, "") // Latin combining marks
    .replace(/[ً-ْـ]/g, "") // Arabic harakat + tatweel
    .replace(SCRIPT_FOLD_RE, (c) => SCRIPT_FOLD[c] ?? c)
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function tokenSet(normalized: string): Set<string> {
  return new Set(normalized.split(" ").filter((t) => t.length >= 2));
}

/**
 * Dice coefficient over token sets, with a containment shortcut for the common
 * case where the node label is a trimmed version of the item's sentence.
 * Returns 0..1.
 */
export function similarity(a: string, b: string): number {
  const na = normalizeForMatch(a);
  const nb = normalizeForMatch(b);
  if (!na || !nb) return 0;
  if (na === nb) return 1;

  const ta = tokenSet(na);
  const tb = tokenSet(nb);
  if (ta.size === 0 || tb.size === 0) return 0;

  // One is a clean substring of the other and long enough to mean it.
  const shorter = na.length <= nb.length ? na : nb;
  const longer = na.length <= nb.length ? nb : na;
  const shorterTokens = shorter === na ? ta : tb;
  if (shorterTokens.size >= 3 && longer.includes(shorter)) return 0.9;

  let shared = 0;
  ta.forEach((t) => {
    if (tb.has(t)) shared += 1;
  });
  return (2 * shared) / (ta.size + tb.size);
}

// ----------------------------------------------------------------------
// Candidate pools + resolution
// ----------------------------------------------------------------------

export type MindmapEvidenceKind = "decision" | "action" | "risk" | "point";

type Candidate = {
  kind: MindmapEvidenceKind;
  text: string;
  /** Raw stored anchor object (may carry the verbatim `quote`). */
  raw: unknown;
  stampSecs: number | null;
};

export type MindmapEvidence = {
  kind: MindmapEvidenceKind;
  /** The matched item's own text — a node label is a restatement of it. */
  itemText: string;
  /** Verbatim transcript quote, when the anchor stored one. Never invented. */
  quote: string | null;
  /** Playable span, or null when the moment could not be located. */
  anchor: VoiceAnchor | null;
};

/**
 * Score floor and the margin the winner must beat the runner-up by. Both exist
 * to make "no match" a normal outcome: two decisions from the same discussion
 * score alike, and picking the higher one by a hair would be guessing.
 *
 * Calibrated 04-08-2026 against 68 leaf nodes across 5 real meetings (English
 * and Urdu), reading every newly-admitted match rather than trusting the rate:
 *   0.50 / 0.08 -> 54% matched
 *   0.35 / 0.05 -> 75% matched, and all 14 gained matches are correct, each
 *                  with its runner-up far behind (0.46 vs 0.20, 0.49 vs 0.16…)
 *   0.30 / 0.04 -> 81%, but 2 of the 4 it gains pick the WRONG item — a node
 *                  about Salesforce/Asana integration lands on "Integration
 *                  with BI tools is used for dashboarding", with the correct
 *                  item sitting at the runner-up. That is the confident-wrong-
 *                  quote failure this whole file exists to prevent.
 * So 0.35/0.05 is the last setting where the rate improves and precision does
 * not. Do not lower it without re-reading what the change lets in; the rate
 * alone will happily keep climbing past the point of being wrong.
 */
const MATCH_FLOOR = 0.35;
const MATCH_MARGIN = 0.05;

export type MindmapEvidenceIndex = {
  pools: Record<MindmapEvidenceKind, Candidate[]>;
  segmentStarts: number[];
  totalDuration: number;
  recordingUrl: string | null;
};

/** Build once per meeting — the pools are the same for every node. */
export function buildMindmapEvidenceIndex(
  meeting: MeetingDetail,
): MindmapEvidenceIndex {
  // Paired before the empty entries are dropped — indexing the parallel anchor
  // array by a post-filter position hands each decision its NEIGHBOUR's audio.
  // (23-08-2026; see pairDecisions in ./meetingEvidence)
  const decisions = pairDecisions(meeting);

  const pools: Record<MindmapEvidenceKind, Candidate[]> = {
    decision: decisions.map((d) => ({
      kind: "decision" as const,
      text: d.text,
      raw: d.anchorRaw,
      stampSecs: d.stampSecs,
    })),
    action: (meeting.todos ?? [])
      // Dismissed to-dos leave the map too (todoDismiss.ts).
      .filter((t) => !!t?.task && !t?.dismissedAt)
      .map((t) => ({
        kind: "action" as const,
        text: t.task as string,
        raw: t.anchor ?? null,
        stampSecs: numberOrNull(t.timeInSecs),
      })),
    // Risks are a free-text blob; the per-line split is how a risk becomes an
    // addressable item at all. They carry no anchors today — so a matched risk
    // node renders as unanchored rather than borrowing a neighbour's clock.
    risk: (meeting.attention_risks?.trim()
      ? splitRiskLines(meeting.attention_risks)
      : []
    ).map((text) => ({
      kind: "risk" as const,
      text,
      raw: null,
      stampSecs: null,
    })),
    point: (meeting.key_points ?? [])
      .filter((p) => !!p?.point)
      .map((p) => ({
        kind: "point" as const,
        text: p.point as string,
        raw: p.anchor ?? null,
        stampSecs: numberOrNull(p.timeInSecs),
      })),
  };

  return {
    pools,
    segmentStarts: buildSegmentStarts(meeting.transcript),
    totalDuration: Number(meeting.total_duration_seconds ?? 0),
    recordingUrl: meeting.recording_url ?? null,
  };
}

/**
 * Null/empty must NOT become 0 — `Number(null)` is 0, and a 0 here is a real
 * offset, so an item with no stamp would resolve to a confident "00:00" with a
 * play button on it. Exactly the class of invented clock the anchor plan bans.
 */
function numberOrNull(v: unknown): number | null {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) && n >= 0 ? n : null;
}

/**
 * Which pools a node type may draw from. `topic` and `root` draw from none:
 * a topic is a heading the model invented to group things, not a moment
 * somebody spoke, and letting it match the nearest key point would put a quote
 * under a node that never had one.
 */
const POOLS_BY_TYPE: Record<string, MindmapEvidenceKind[]> = {
  decision: ["decision", "point"],
  action: ["action", "point"],
  risk: ["risk", "point"],
};

/** Present and future-proofing: a node that ships its own anchor wins outright. */
function nodeAnchorOverride(node: MindmapNodeData): unknown {
  return (node as unknown as Record<string, unknown>).anchor ?? null;
}

export function resolveMindmapEvidence(
  node: MindmapNodeData,
  index: MindmapEvidenceIndex,
): MindmapEvidence | null {
  const label = node.label?.trim();
  if (!label) return null;

  const own = nodeAnchorOverride(node);
  if (own) {
    return {
      kind: (node.type as MindmapEvidenceKind) ?? "point",
      itemText: label,
      quote: readQuote(own),
      anchor: resolveVoiceAnchor(
        null,
        own,
        index.segmentStarts,
        index.totalDuration,
      ),
    };
  }

  const pools = POOLS_BY_TYPE[node.type ?? ""] ?? [];
  if (pools.length === 0) return null;

  const scored = pools
    .flatMap((kind) => index.pools[kind])
    .map((c) => ({ c, score: similarity(label, c.text) }))
    .sort((a, b) => b.score - a.score);

  const best = scored[0];
  const runnerUp = scored[1]?.score ?? 0;

  if (!best || best.score < MATCH_FLOOR) return null;
  if (best.score - runnerUp < MATCH_MARGIN && runnerUp >= MATCH_FLOOR) {
    // Two items describe this node about equally well. Saying nothing is the
    // honest answer; showing the marginally-higher one would be a guess
    // wearing a verified quote's clothes.
    return null;
  }

  return {
    kind: best.c.kind,
    itemText: best.c.text,
    quote: readQuote(best.c.raw),
    anchor: resolveVoiceAnchor(
      best.c.stampSecs,
      best.c.raw,
      index.segmentStarts,
      index.totalDuration,
    ),
  };
}

/**
 * The verbatim transcript span the anchor was **verified** on.
 *
 * `anchor.quote` is populated on every tier of the backend resolver, but it
 * only *means* the same thing on two of them (anchor.service.mjs
 * `resolveItemAnchor`):
 *   - `source: "quote"` — the model's verbatim quote, re-found in the
 *     transcript. Real evidence.
 *   - `source: "text"`  — the item's own wording located by IDF matching. The
 *     words were found; only the seconds are approximate. Real evidence.
 *   - `source: "llm-id" | "bookmark"` — **unverified**. The "quote" is just
 *     whatever text sits at the segment the model claimed, or at a stored
 *     second. Nothing was matched; the span was taken on trust.
 *
 * Measured on 5 real meetings (04-08-2026): **75 of 90 stored anchors are
 * `llm-id`**. Rendering those as a quotation is precisely the confident-wrong-
 * quote failure this file exists to prevent — a decision about branded
 * residences came out quoting "تو جس brand کا beach club لا رہے ہیں نا sir".
 * So the unverified tiers return no quote; the card then shows the item's own
 * text as a "Moment" and the span stays playable, honestly labelled.
 */
const VERIFIED_SOURCES = new Set(["quote", "text"]);

function readQuote(raw: unknown): string | null {
  if (!raw || typeof raw !== "object") return null;
  const a = raw as Record<string, unknown>;
  if (a.confidence === "none") return null;
  if (typeof a.source === "string" && !VERIFIED_SOURCES.has(a.source)) {
    return null;
  }
  const q = typeof a.quote === "string" ? a.quote.trim() : "";
  return q.length > 0 ? q : null;
}
